/**
 * formKit — shared primitives for the stock-first, progressive-disclosure forms
 * (JobFormV2 pattern). Used by BuffingFormV2; JobFormV2 keeps its own inline
 * copies for now. Module-scope so component identities stay stable across
 * renders (defining these inside a component remounts every input on keystroke).
 */

import React, { useRef } from 'react';

/* ---- design tokens ---------------------------------------------------- */
export const TEAL = '#4A7A91', TEAL_TINT = '#e8f2f7', NAVY = '#1e3a4a';
export const YELLOW = '#FFD000', GREEN = '#059669', CHECK = '#16a34a';
export const PAGE = '#f4f6f8', LINE = '#e5e7eb', TRACK = '#eceef0';
export const G400 = '#9ca3af', G500 = '#6b7280', G700 = '#374151', G300 = '#d1d5db';
export const MONO = 'ui-monospace, Menlo, monospace';

export const PUB2 = process.env.PUBLIC_URL + '/brand-logos';
export const BRAND_LOGO_MAP = { 'KIA CARS': 'kia.png', 'GWM CARS': 'gwm.png', 'GAC CARS': 'gac.png', 'SUZUKI CARS': 'suzuki.png', 'USED CARS': 'used.png' };

// A NEW-car dealership stock. Kia/GWM = letter + 6 digits; GAC = A + one
// alphanumeric + 5 digits (covers both A123456 and the newer AF12602 / AK12356
// VIN-based numbers); Suzuki = 6 digits.
export const isNewCarStock = (s) => {
  const t = String(s || '').trim().toUpperCase();
  return /^[KG]\d{6}$/.test(t) || /^A[A-Z0-9]\d{5}$/.test(t) || /^\d{6}$/.test(t);
};

// Brand department from the stock prefix (K→Kia, G→GWM, A→GAC, 6 digits→Suzuki,
// 5 digits→Used). GAC now allows a second letter (AF12602), so match any A-prefix.
export function brandFromStock(stock, prefillDept) {
  if (prefillDept) return prefillDept;
  const s = String(stock || '').trim().toUpperCase();
  if (/^K\d/.test(s)) return 'KIA CARS';
  if (/^G\d/.test(s)) return 'GWM CARS';
  if (/^A/.test(s)) return 'GAC CARS';
  if (/^\d{6}/.test(s)) return 'SUZUKI CARS';
  if (/^\d{5}$/.test(s)) return 'USED CARS';
  return null;
}
// Expected length of a segmented stock: letter-first → 7; digit-first → digitLen.
export const stockLen = (s, digitLen) => (/^\d/.test(String(s || '')) ? (digitLen || 6) : 7);
export const fmtDMY = (iso) => iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '';

/* ---- form header (Detail Queue intro-block style) --------------------- */
const WD = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MO = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const todayKicker = () => { const d = new Date(); return `${WD[d.getDay()]} ${d.getDate()} ${MO[d.getMonth()]}`; };

// Blue intro block matching the Detail Queue screen, with the section-progress
// bar kept. No search field, no week navigator.
export function FormHeader({ title, kicker, doneCount, total }) {
  return (
    <div style={{ background: '#dfe9ee', padding: '16px 20px 18px' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.2, color: '#7d97a5', marginBottom: 6, textTransform: 'uppercase' }}>
          {kicker || todayKicker()}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.5, color: NAVY }}>{title}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#7d97a5', fontVariantNumeric: 'tabular-nums' }}>{doneCount}/{total} completed</span>
        </div>
        <div style={{ height: 5, background: '#c3d4dc', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.round(doneCount / total * 100)}%`, background: YELLOW, borderRadius: 999, transition: 'width .3s' }} />
        </div>
      </div>
    </div>
  );
}

/* ---- shared chip ------------------------------------------------------ */
export const chip = (active, big) => ({
  fontSize: big ? 13 : 11, fontWeight: 700, letterSpacing: 0.3, padding: big ? '9px 14px' : '6px 10px',
  borderRadius: 8, cursor: 'pointer', textTransform: 'uppercase', lineHeight: 1.1, userSelect: 'none',
  border: `1.5px solid ${active ? TEAL : LINE}`, background: active ? TEAL_TINT : '#fff', color: active ? NAVY : G700,
});

/* ---- collapsible section wrapper -------------------------------------- */
export function SectionShell({ n, title, open, done, locked, summary, onEdit, children }) {
  if (locked) {
    return (
      <div style={{ background: '#fff', padding: '15px 16px', opacity: 0.5, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15 }}>🔒</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400 }}>{n} · {title}</span>
      </div>
    );
  }
  if (done && !open) {
    return (
      <div onClick={onEdit} style={{ background: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: CHECK, color: '#fff', fontSize: 12, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>✓</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, flex: 'none' }}>{n} · {title}</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{summary}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: TEAL, flex: 'none' }}>Edit</span>
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', padding: '16px' }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, marginBottom: 12 }}>{n} · {title}</div>
      {children}
    </div>
  );
}

/* ---- adaptive segmented stock input ----------------------------------- */
export function SegmentedStock({ value, onChange, onComplete, digitLen }) {
  const refs = useRef([]);
  const firstIsDigit = /^\d/.test(value || '');
  const dl = digitLen || 6;
  const n = (value && firstIsDigit) ? dl : 7;
  const chars = Array.from({ length: n }, (_, i) => (value || '')[i] || '');
  const setChar = (i, c) => {
    const arr = [...chars]; arr[i] = c;
    const nn = (arr[0] && /^\d/.test(arr[0])) ? dl : 7;
    const joined = arr.slice(0, nn).join('').toUpperCase();
    onChange(joined);
    if (c && i < nn - 1) refs.current[i + 1] && refs.current[i + 1].focus();
    if (joined.length === nn) { setTimeout(() => onComplete && onComplete(joined), 0); if (refs.current[i]) refs.current[i].blur(); }
  };
  const onKey = (i, e) => {
    if (e.key === 'Backspace' && !chars[i] && i > 0) refs.current[i - 1] && refs.current[i - 1].focus();
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {chars.map((c, i) => {
        const filled = !!c, isFirst = i === 0;
        return (
          <input key={i} ref={el => { refs.current[i] = el; }} value={c} maxLength={1}
            inputMode={(i === 0 || (i === 1 && /^A/i.test(value || ''))) ? 'text' : 'numeric'}
            onChange={e => {
              let v = e.target.value.slice(-1);
              // Box 0 and — for GAC (A-prefix) — box 1 accept a letter; the rest digits.
              const alnum = i === 0 || (i === 1 && /^A/i.test(value || ''));
              v = alnum ? v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : v.replace(/[^0-9]/g, '');
              setChar(i, v);
            }}
            onKeyDown={e => onKey(i, e)}
            style={{
              width: 38, height: 48, textAlign: 'center', fontSize: 20, fontWeight: 800, fontFamily: MONO,
              color: isFirst && filled ? NAVY : '#1a1a2e', borderRadius: 10, outline: 'none', boxSizing: 'border-box',
              border: `1.5px solid ${(isFirst && filled) ? TEAL : LINE}`,
              background: (isFirst && filled) ? TEAL_TINT : '#fff', textTransform: 'uppercase',
            }} />
        );
      })}
    </div>
  );
}
